/**
 * IF-06 v2 기계 엔티티 상태 · ACK 기준 델타(SR-NET-02) · FullState v2(SR-NET-04).
 *
 * 엔티티 = 기계 한 대. 정수 상태(Int32) — 기계마다 [px py pz rot | 바퀴 θ… | 기타 몸체(rx ry rz rrot)…]
 *  - 차체 위치: 2 mm 단위(i16 범위 ±65.5 m) · 회전: smallest-three 32 bit
 *  - 바퀴: 차체에 대한 상대 회전 = 바퀴 축 둘레 각 θ(16 bit, 2π/65536) — 쉴 때 바퀴·차체 회전이 같다(PlayRig)
 *    바퀴 위치 = 차체 + 회전(차체)·relPos(틀) — 관절 유격은 무시(시각 오차 mm 이하)
 *  - 기타(서브 · 키네마틱): 차체 기준 상대 위치(2 mm) · 상대 회전(32 bit) — 차체에 붙은 부품은 한 번 보내면 끝
 * 델타: 마지막 ACK 틱에 **그 연결에 보낸 상태**를 기준으로, 불감대(위치 2 양자 · 회전 0.5°)를 넘은 필드만.
 *  서버는 연결마다 '보낸 상태'를 틱별로 기억하고 클라이언트는 받은 상태를 틱별로 기억한다 — 두 쪽이 비트 단위로 같다.
 *
 * S→C "sn" v2: [ver u8][tick u32][baseTick u32][flags u8][n u8] n×Entity [a u8] a×Ack5
 *   Entity: [machine u8][mask u8] + (mask 1: 위치 델타 i8×3 | mask 2: 위치 i16×3) + (4: 회전 u32) + (8: 바퀴 u16×k)
 *           + (16: [m u8] m×([기타 번호 u8][i16×3][u32]))
 *   baseTick 0 = 기준 없음(모든 필드 절대값)
 * C→S "ak" v2: [ver u8][tick u32] — 받은 스냅샷 틱(서버는 가장 최근 것을 기준으로)
 * S→C "fs" v2: [ver u8][tick u32][jsonLen u32][json][envN u16] envN×Body12 [stateLen u32] stateLen×i32
 */
import {
  BODY_BYTES,
  packQuat,
  POS_Q,
  PROTOCOL_VERSION,
  readBody,
  unpackQuat,
  writeBody,
  type Ack,
} from "./index";

export type Vec3 = readonly [number, number, number];

/** 틀(모형) 안 몸체 하나 — 셸 렌더(BodyDesc 모양) + 복원에 쓰는 relPos · axis */
export interface TemplateBody {
  readonly kind: string;
  readonly nodes?: readonly string[];
  readonly shape?: unknown;
  readonly surface?: string;
  readonly rest?: readonly number[];
  /** 바퀴 — 차체 지역 좌표의 바퀴 중심(m, mm 로 반올림) */
  readonly relPos?: Vec3;
  /** 바퀴 — 차체 지역 좌표의 회전축(단위) */
  readonly axis?: Vec3;
}

export interface EntityLayout {
  readonly machine: number;
  readonly bodyStart: number;
  readonly bodyCount: number;
  /** 몸체 k 의 역할: 0 차체 · 1 바퀴 · 2 기타 */
  readonly roles: readonly (0 | 1 | 2)[];
  readonly relPos: readonly (Vec3 | null)[];
  readonly axis: readonly (Vec3 | null)[];
  readonly wheels: number;
  readonly others: number;
  /** 상태 배열에서 이 기계의 시작 */
  readonly offset: number;
  readonly size: number;
}

export function layoutOf(
  machines: readonly { machine: number; bodyStart: number; bodyCount: number; template: number }[],
  templates: readonly (readonly TemplateBody[])[],
): { layouts: EntityLayout[]; stateSize: number } {
  let offset = 0;
  const layouts = machines.map((m) => {
    const tpl = templates[m.template] ?? [];
    const roles: (0 | 1 | 2)[] = [];
    const relPos: (Vec3 | null)[] = [];
    const axis: (Vec3 | null)[] = [];
    let wheels = 0;
    let others = 0;
    for (let k = 0; k < m.bodyCount; k++) {
      const b = tpl[k];
      const role: 0 | 1 | 2 = k === 0 ? 0 : b?.kind === "wheel" && b.axis && b.relPos ? 1 : 2;
      roles.push(role);
      relPos.push(role === 1 ? b!.relPos! : null);
      axis.push(role === 1 ? b!.axis! : null);
      if (role === 1) wheels++;
      else if (role === 2) others++;
    }
    const size = 4 + wheels + others * 4;
    const l: EntityLayout = { ...m, roles, relPos, axis, wheels, others, offset, size };
    offset += size;
    return l;
  });
  return { layouts, stateSize: offset };
}

// ── 쿼터니언 셈(x,y,z,w) ──────────────────────────────────────────────

type Q = [number, number, number, number];
const qmul = (a: Q, b: Q): Q => [
  a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
  a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
  a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
  a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
];
const qconj = (a: Q): Q => [-a[0], -a[1], -a[2], a[3]];
function qrot(q: Q, v: Vec3): [number, number, number] {
  const r = qmul(qmul(q, [v[0], v[1], v[2], 0]), qconj(q));
  return [r[0], r[1], r[2]];
}
const qAt = (t: ArrayLike<number>, o: number): Q => [t[o]!, t[o + 1]!, t[o + 2]!, t[o + 3]!];
const unpack = (p: number): Q => {
  const q = [0, 0, 0, 0];
  unpackQuat(p >>> 0, q, 0);
  return q as Q;
};

const POS_MAX = 32767;
const qp = (m: number) => Math.max(-POS_MAX, Math.min(POS_MAX, Math.round(m / POS_Q)));
const ANG_Q = 65536 / (2 * Math.PI);

/** 몸체 자세(m · transforms) → 정수 상태(out) */
export function quantizeState(
  t: Float32Array,
  layouts: readonly EntityLayout[],
  out: Int32Array,
): void {
  for (const l of layouts) {
    const c = l.bodyStart * 7;
    const qc: Q = qAt(t, c + 3);
    let o = l.offset;
    out[o++] = qp(t[c]!);
    out[o++] = qp(t[c + 1]!);
    out[o++] = qp(t[c + 2]!);
    const pc = packQuat(qc[0], qc[1], qc[2], qc[3]);
    out[o++] = pc | 0;
    // 복원이 쓰는 것과 같은 (양자화된) 차체 회전으로 상대값을 잰다
    const qcq = unpack(pc);
    const inv = qconj(qcq);
    for (let k = 1; k < l.bodyCount; k++) {
      const b = (l.bodyStart + k) * 7;
      const qb = qAt(t, b + 3);
      const rel = qmul(inv, qb);
      if (l.roles[k] === 1) {
        const a = l.axis[k]!;
        const s = rel[0] * a[0] + rel[1] * a[1] + rel[2] * a[2];
        const th = 2 * Math.atan2(s, rel[3]);
        out[o++] = Math.round(th * ANG_Q) & 0xffff;
      } else {
        const dp = qrot(inv, [t[b]! - t[c]!, t[b + 1]! - t[c + 1]!, t[b + 2]! - t[c + 2]!]);
        out[o++] = qp(dp[0]);
        out[o++] = qp(dp[1]);
        out[o++] = qp(dp[2]);
        out[o++] = packQuat(rel[0], rel[1], rel[2], rel[3]) | 0;
      }
    }
  }
}

/** 정수 상태 → 몸체 자세(m) — 기계 몸체만 쓴다(환경 몸체 자리는 그대로) */
export function reconstructBodies(
  s: Int32Array,
  layouts: readonly EntityLayout[],
  out: Float32Array,
): void {
  for (const l of layouts) {
    let o = l.offset;
    const c = l.bodyStart * 7;
    if (c + 7 > out.length) continue;
    const pc: [number, number, number] = [s[o]! * POS_Q, s[o + 1]! * POS_Q, s[o + 2]! * POS_Q];
    const qc = unpack(s[o + 3]!);
    o += 4;
    out[c] = pc[0];
    out[c + 1] = pc[1];
    out[c + 2] = pc[2];
    out[c + 3] = qc[0];
    out[c + 4] = qc[1];
    out[c + 5] = qc[2];
    out[c + 6] = qc[3];
    for (let k = 1; k < l.bodyCount; k++) {
      const b = (l.bodyStart + k) * 7;
      let q: Q;
      let p: [number, number, number];
      if (l.roles[k] === 1) {
        const th = (((s[o++]! & 0xffff) << 16) >> 16) / ANG_Q; // i16 로 되돌려 ±π
        const a = l.axis[k]!;
        const h = th / 2;
        const sh = Math.sin(h);
        q = qmul(qc, [a[0] * sh, a[1] * sh, a[2] * sh, Math.cos(h)]);
        const r = qrot(qc, l.relPos[k]!);
        p = [pc[0] + r[0], pc[1] + r[1], pc[2] + r[2]];
      } else {
        const r = qrot(qc, [s[o]! * POS_Q, s[o + 1]! * POS_Q, s[o + 2]! * POS_Q]);
        p = [pc[0] + r[0], pc[1] + r[1], pc[2] + r[2]];
        q = qmul(qc, unpack(s[o + 3]!));
        o += 4;
      }
      out[b] = p[0];
      out[b + 1] = p[1];
      out[b + 2] = p[2];
      out[b + 3] = q[0];
      out[b + 4] = q[1];
      out[b + 5] = q[2];
      out[b + 6] = q[3];
    }
  }
}

// ── 불감대 ────────────────────────────────────────────────────────────

/** 위치 불감대(양자) — 2 양자(4 mm) 미만의 떨림은 보내지 않는다 */
export const POS_DEADBAND_Q = 2;
const ROT_DEADBAND_DOT = Math.cos((0.5 * Math.PI) / 180 / 2);
/** 바퀴 불감대(16 bit 단위) — 0.5° */
const WHEEL_DEADBAND = Math.round(((0.5 * Math.PI) / 180) * ANG_Q);

const rotChanged = (a: number, b: number) => {
  if (a === b) return false;
  const qa = unpack(a);
  const qb = unpack(b);
  return Math.abs(qa[0] * qb[0] + qa[1] * qb[1] + qa[2] * qb[2] + qa[3] * qb[3]) < ROT_DEADBAND_DOT;
};
const posChanged = (a: Int32Array, b: Int32Array, o: number) =>
  Math.abs(a[o]! - b[o]!) >= POS_DEADBAND_Q ||
  Math.abs(a[o + 1]! - b[o + 1]!) >= POS_DEADBAND_Q ||
  Math.abs(a[o + 2]! - b[o + 2]!) >= POS_DEADBAND_Q;
const wheelChanged = (a: number, b: number) => {
  const d = Math.abs((((a - b) & 0xffff) << 16) >> 16);
  return d >= WHEEL_DEADBAND;
};

// ── 델타 인코딩 ───────────────────────────────────────────────────────

export const M_POS_DELTA = 1;
export const M_POS_ABS = 2;
export const M_ROT = 4;
export const M_WHEELS = 8;
export const M_OTHERS = 16;

/**
 * 스냅샷 v2 인코딩 — base(그 연결에 baseTick 에 보낸 상태, 없으면 null) 대비 truth 에서 바뀐 필드만.
 * sent 에 '이번에 보낸 상태'(= 클라이언트가 복원할 상태)를 쓴다 — 다음 델타의 기준.
 */
export function encodeDelta(
  tick: number,
  baseTick: number,
  flags: number,
  layouts: readonly EntityLayout[],
  base: Int32Array | null,
  truth: Int32Array,
  sent: Int32Array,
  acks: readonly Ack[],
): Uint8Array {
  if (base) sent.set(base);
  else sent.set(truth);
  // 최대 크기로 잡고 자른다
  let cap = 12 + 1 + Math.min(255, acks.length) * 5;
  for (const l of layouts) cap += 2 + 6 + 4 + l.wheels * 2 + 1 + l.others * 11;
  const b = new Uint8Array(cap);
  const v = new DataView(b.buffer);
  v.setUint8(0, PROTOCOL_VERSION);
  v.setUint32(1, tick >>> 0, true);
  v.setUint32(5, base ? baseTick >>> 0 : 0, true);
  v.setUint8(9, flags);
  let at = 11;
  let n = 0;
  for (const l of layouts) {
    const o = l.offset;
    let mask = 0;
    const start = at;
    at += 2;
    // 위치
    if (!base || posChanged(truth, base, o)) {
      const d0 = truth[o]! - (base?.[o] ?? 0);
      const d1 = truth[o + 1]! - (base?.[o + 1] ?? 0);
      const d2 = truth[o + 2]! - (base?.[o + 2] ?? 0);
      if (base && Math.max(Math.abs(d0), Math.abs(d1), Math.abs(d2)) <= 127) {
        mask |= M_POS_DELTA;
        v.setInt8(at, d0);
        v.setInt8(at + 1, d1);
        v.setInt8(at + 2, d2);
        at += 3;
      } else {
        mask |= M_POS_ABS;
        v.setInt16(at, truth[o]!, true);
        v.setInt16(at + 2, truth[o + 1]!, true);
        v.setInt16(at + 4, truth[o + 2]!, true);
        at += 6;
      }
      sent[o] = truth[o]!;
      sent[o + 1] = truth[o + 1]!;
      sent[o + 2] = truth[o + 2]!;
    }
    if (!base || rotChanged(truth[o + 3]!, base[o + 3]!)) {
      mask |= M_ROT;
      v.setUint32(at, truth[o + 3]! >>> 0, true);
      at += 4;
      sent[o + 3] = truth[o + 3]!;
    }
    // 바퀴(한 개라도 넘으면 모두 — 같이 돈다)
    let wo = o + 4;
    let wheelsChanged = !base;
    if (base)
      for (let k = 0; k < l.wheels && !wheelsChanged; k++)
        wheelsChanged = wheelChanged(truth[wo + k]!, base[wo + k]!);
    if (wheelsChanged && l.wheels) {
      mask |= M_WHEELS;
      for (let k = 0; k < l.wheels; k++) {
        v.setUint16(at, truth[wo + k]! & 0xffff, true);
        at += 2;
        sent[wo + k] = truth[wo + k]!;
      }
    }
    wo += l.wheels;
    // 기타 몸체
    const countAt = at;
    let m = 0;
    at += 1;
    for (let k = 0; k < l.others; k++) {
      const x = wo + k * 4;
      if (base && !posChanged(truth, base, x) && !rotChanged(truth[x + 3]!, base[x + 3]!)) continue;
      v.setUint8(at, k);
      v.setInt16(at + 1, truth[x]!, true);
      v.setInt16(at + 3, truth[x + 1]!, true);
      v.setInt16(at + 5, truth[x + 2]!, true);
      v.setUint32(at + 7, truth[x + 3]! >>> 0, true);
      at += 11;
      for (let j = 0; j < 4; j++) sent[x + j] = truth[x + j]!;
      m++;
    }
    if (m) {
      mask |= M_OTHERS;
      v.setUint8(countAt, m);
    } else at = countAt;
    if (!mask) {
      at = start;
      continue;
    }
    v.setUint8(start, l.machine);
    v.setUint8(start + 1, mask);
    n++;
  }
  v.setUint8(10, n);
  const a = Math.min(255, acks.length);
  v.setUint8(at++, a);
  for (let k = 0; k < a; k++) {
    v.setUint8(at, acks[k]!.machine);
    v.setUint32(at + 1, acks[k]!.inputTick >>> 0, true);
    at += 5;
  }
  return b.slice(0, at);
}

export interface DecodedDelta {
  readonly tick: number;
  readonly baseTick: number;
  readonly flags: number;
  /** 바뀐 기계 번호 */
  readonly machines: number[];
  readonly acks: Ack[];
}

/**
 * 스냅샷 v2 복원 — out = base(baseTick 에 받은 상태 · 0 이면 없음) + 바뀐 필드. base 가 필요한데 없으면 null.
 */
export function decodeDelta(
  bytes: Uint8Array,
  layouts: readonly EntityLayout[],
  baseOf: (tick: number) => Int32Array | null,
  out: Int32Array,
): DecodedDelta | null {
  if (bytes.byteLength < 12) return null;
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (v.getUint8(0) !== PROTOCOL_VERSION) return null;
  const tick = v.getUint32(1, true);
  const baseTick = v.getUint32(5, true);
  if (baseTick) {
    const base = baseOf(baseTick);
    if (!base) return null;
    out.set(base);
  }
  const byMachine = new Map(layouts.map((l) => [l.machine, l]));
  const n = v.getUint8(10);
  let at = 11;
  const machines: number[] = [];
  for (let e = 0; e < n; e++) {
    const l = byMachine.get(v.getUint8(at));
    const mask = v.getUint8(at + 1);
    at += 2;
    if (!l) return null;
    machines.push(l.machine);
    const o = l.offset;
    if (mask & M_POS_DELTA) {
      out[o] = out[o]! + v.getInt8(at);
      out[o + 1] = out[o + 1]! + v.getInt8(at + 1);
      out[o + 2] = out[o + 2]! + v.getInt8(at + 2);
      at += 3;
    } else if (mask & M_POS_ABS) {
      out[o] = v.getInt16(at, true);
      out[o + 1] = v.getInt16(at + 2, true);
      out[o + 2] = v.getInt16(at + 4, true);
      at += 6;
    }
    if (mask & M_ROT) {
      out[o + 3] = v.getUint32(at, true) | 0;
      at += 4;
    }
    const wo = o + 4;
    if (mask & M_WHEELS) {
      for (let k = 0; k < l.wheels; k++) {
        out[wo + k] = v.getUint16(at, true);
        at += 2;
      }
    }
    if (mask & M_OTHERS) {
      const m = v.getUint8(at++);
      for (let j = 0; j < m; j++) {
        const x = wo + l.wheels + v.getUint8(at) * 4;
        out[x] = v.getInt16(at + 1, true);
        out[x + 1] = v.getInt16(at + 3, true);
        out[x + 2] = v.getInt16(at + 5, true);
        out[x + 3] = v.getUint32(at + 7, true) | 0;
        at += 11;
      }
    }
  }
  const a = v.getUint8(at++);
  const acks: Ack[] = [];
  for (let k = 0; k < a && at + 5 <= bytes.byteLength; k++, at += 5)
    acks.push({ machine: v.getUint8(at), inputTick: v.getUint32(at + 1, true) });
  return { tick, baseTick, flags: v.getUint8(9), machines, acks };
}

// ── ACK · FullState v2 ───────────────────────────────────────────────

export function encodeAck(tick: number): Uint8Array {
  const b = new Uint8Array(5);
  const v = new DataView(b.buffer);
  v.setUint8(0, PROTOCOL_VERSION);
  v.setUint32(1, tick >>> 0, true);
  return b;
}
export function decodeAck(bytes: Uint8Array): number | null {
  if (bytes.byteLength !== 5) return null;
  const v = new DataView(bytes.buffer, bytes.byteOffset, 5);
  return v.getUint8(0) === PROTOCOL_VERSION ? v.getUint32(1, true) : null;
}

/** envIndex: 환경(기계 아닌) 몸체 번호 · envT: 그 자세(transforms 전체) · state: 기계 정수 상태 */
export function encodeFullStateV2(
  tick: number,
  meta: unknown,
  envIndex: readonly number[],
  envT: Float32Array,
  state: Int32Array,
): Uint8Array {
  const json = new TextEncoder().encode(JSON.stringify(meta));
  const b = new Uint8Array(
    1 + 4 + 4 + json.byteLength + 2 + envIndex.length * BODY_BYTES + 4 + state.length * 4,
  );
  const v = new DataView(b.buffer);
  v.setUint8(0, PROTOCOL_VERSION);
  v.setUint32(1, tick >>> 0, true);
  v.setUint32(5, json.byteLength, true);
  b.set(json, 9);
  let at = 9 + json.byteLength;
  v.setUint16(at, envIndex.length, true);
  at += 2;
  for (const i of envIndex) {
    writeBody(v, at, i, envT);
    at += BODY_BYTES;
  }
  v.setUint32(at, state.length, true);
  at += 4;
  for (let k = 0; k < state.length; k++, at += 4) v.setInt32(at, state[k]!, true);
  return b;
}

export function decodeFullStateV2<M = unknown>(
  bytes: Uint8Array,
  bodyCountOf: (meta: M) => number,
): { tick: number; meta: M; transforms: Float32Array; state: Int32Array } | null {
  if (bytes.byteLength < 11) return null;
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (v.getUint8(0) !== PROTOCOL_VERSION) return null;
  const len = v.getUint32(5, true);
  const meta = JSON.parse(new TextDecoder().decode(bytes.subarray(9, 9 + len))) as M;
  let at = 9 + len;
  const transforms = new Float32Array(bodyCountOf(meta) * 7);
  for (let i = 0; i + 6 < transforms.length; i += 7) transforms[i + 6] = 1;
  const envN = v.getUint16(at, true);
  at += 2;
  for (let k = 0; k < envN; k++, at += BODY_BYTES) readBody(v, at, transforms);
  const sl = v.getUint32(at, true);
  at += 4;
  const state = new Int32Array(sl);
  for (let k = 0; k < sl; k++, at += 4) state[k] = v.getInt32(at, true);
  return { tick: v.getUint32(1, true), meta, transforms, state };
}
